import { useEffect, useState } from "react";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { getServiceStatusAPI } from "kontrolEdge/views/requests/request";
import { ServiceStatus } from "kontrolEdge/views/OverviewComponents/Types";
import {
  SERVICE_KINDS,
  serviceDetailPath,
} from "kontrolEdge/views/OverviewComponents/actionCenter/overviewModel";
import { DiagramNode, DiagramNodeKind } from "./diagramSpec";

/**
 * Where an element of a drawing leads when clicked — and, through
 * `data-view-href`, what "Add view" opens.
 *
 * Archi names a service by its subscription id ("10-7E2C9A"), the public id
 * the Customer API uses. The dashboard's detail pages are addressed by their
 * own ids, so the drawing borrows the Overview's service list, which carries
 * both, and builds each address with the Overview's own `serviceDetailPath`.
 * An element that is not one service (a cloud, a provider, a site) opens the
 * list it belongs to.
 */

/** Subscription id (lower-cased) → its detail page. */
export type ServiceLinks = Map<string, string>;

const LIST_BY_KIND: Partial<Record<DiagramNodeKind, string>> = {
  port: "/kontrol/port",
  site: "/kontrol/port",
  datacenter: "/kontrol/port",
  cloud: "/kontrol/cloud-connectivity",
  service: "/kontrol/cloud-connectivity",
  internet: "/kontrol/ipx",
  dci: "/kontrol/datacenter",
  backbone: "/kontrol/backbone",
  firewall: "/kontrol/fwaas",
  sdwan: "/kontrol/sdwan-hub",
  // The Customer API names a reflector by its public uuid, not the numeric id
  // its detail route takes, so a reflector opens the list it is in.
  reflector: "/reflector-v2/management/bgp-reflectors",
};

/** "10-7E2C9A", "40-AC53D9": a Kontrol Edge subscription id, as it appears in a label. */
const SUBSCRIPTION_ID = /\b\d{2}-[0-9A-F]{6}\b/i;

/** The services the lens shows, keyed by subscription id; empty until read. */
export function useServiceLinks(
  tenantId: number | null,
  enabled: boolean,
): ServiceLinks {
  const api = useAxiosAuth0();
  const [links, setLinks] = useState<ServiceLinks>(new Map());

  useEffect(() => {
    if (!enabled || !tenantId) return;
    let cancelled = false;
    getServiceStatusAPI(api, tenantId)
      .then((res) => {
        if (cancelled) return;
        const statuses = (res.data?.data ?? {}) as Partial<ServiceStatus>;
        const next: ServiceLinks = new Map();
        for (const kind of SERVICE_KINDS) {
          for (const item of statuses[kind.key] ?? []) {
            if (item.subscriptionId)
              next.set(
                item.subscriptionId.trim().toLowerCase(),
                serviceDetailPath(kind, item),
              );
          }
        }
        setLinks(next);
      })
      // Without the list, every element still opens its list page.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, tenantId, enabled]);

  return links;
}

/** The page a drawn element opens, or null when it has none (a customer). */
export function nodeHref(
  node: DiagramNode,
  links: ServiceLinks,
): string | null {
  const id =
    node.ref ?? SUBSCRIPTION_ID.exec(`${node.label} ${node.detail ?? ""}`)?.[0];
  const detail = id ? links.get(id.trim().toLowerCase()) : undefined;
  return detail ?? LIST_BY_KIND[node.kind] ?? null;
}
